import { useState, type InputHTMLAttributes, type ReactNode } from 'react';

import useIsPWA from '@/hooks/use-is-pwa';
import { errorMessage, type FieldErrorLike } from '@/types/form';

export interface SimpleDatePickerProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
    name: string;
    label?: ReactNode;
    value?: Date | string | null;
    /** Reçoit une `Date` (minuit UTC du jour saisi, comme `new Date('yyyy-MM-dd')`) ou `null`. */
    setValue: (name: string, value: Date | null) => void;
    error?: FieldErrorLike;
    required?: boolean;
    maxDate?: Date;
}

const SimpleDatePicker = ({ label, name, value, setValue, error, required = false, maxDate, ...other }: SimpleDatePickerProps) => {
    const [focused, setFocused] = useState(false);
    const isPWA = useIsPWA();

    const formattedValue = value instanceof Date ? value.toISOString().split('T')[0] : typeof value === 'string' ? value : '';

    const formattedMaxDate = maxDate instanceof Date ? maxDate.toISOString().split('T')[0] : undefined;

    const isActive = focused || formattedValue;

    const errorId = `${name}-error`;

    return (
        <div className="relative w-full pwa-date-wrapper">
            <input
                type="date"
                id={name}
                name={name}
                className={`peer w-full px-6 py-4 rounded-lg border border-border bg-transparent text-base transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${error ? 'border-destructive focus:ring-destructive/20 focus:border-destructive' : ''} ${!isActive ? 'text-transparent' : ''}`}
                value={formattedValue}
                onChange={(e) => {
                    const newDate = e.target.value ? new Date(e.target.value) : null;
                    setValue(name, newDate);
                }}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                max={formattedMaxDate}
                required={required}
                aria-required={required || undefined}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                {...other}
            />
            {label && (
                <label
                    htmlFor={name}
                    className={`absolute left-3 ${isPWA ? (isActive ? 'bg-white' : 'bg-transparent') : 'bg-card'} px-1 text-muted-foreground transition-all duration-200 pointer-events-none ${isActive ? 'text-xs -top-2 text-primary' : 'text-sm top-4'} ${error ? 'text-destructive' : ''}`}
                >
                    {label}
                    {required ? (
                        <sup className="text-destructive ml-0.5" aria-label="obligatoire">
                            *
                        </sup>
                    ) : (
                        ''
                    )}
                </label>
            )}
            {error && (
                <p id={errorId} className="mt-1 text-xs text-destructive italic" role="alert" aria-live="assertive">
                    {errorMessage(error)}
                </p>
            )}
        </div>
    );
};

export default SimpleDatePicker;
